import { useState, useCallback, memo, useLayoutEffect, useRef, startTransition } from 'react';
import { flushSync } from 'react-dom';
import { Switch } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { NationalProductsDetails } from '@/types/nationalProducts.types';
import { CustomTableMeta } from '@/components/Tables/TableCard/TableCard.types';

// Custom comparison function for memo - only re-render if priceLock actually changed
const arePriceLockPropsEqual = (
  prevProps: CellContext<NationalProductsDetails, unknown>,
  nextProps: CellContext<NationalProductsDetails, unknown>,
) => {
  // Only re-render if the actual lock value changed or if row index changed
  const prevLock = Boolean(prevProps.row.original.priceLock);
  const nextLock = Boolean(nextProps.row.original.priceLock);
  const prevRowIndex = prevProps.row.index;
  const nextRowIndex = nextProps.row.index;

  return prevLock === nextLock && prevRowIndex === nextRowIndex;
};

export const PriceLockSwitchCell = memo(({ row, table, column }: CellContext<NationalProductsDetails, unknown>) => {
  const { original } = row;
  const { priceLock } = original;
  const { updateData } = table.options.meta as CustomTableMeta<NationalProductsDetails>;

  // Use local state for instant visual feedback - updates immediately on click
  const [checked, setChecked] = useState(Boolean(priceLock));

  // Track previous value to avoid unnecessary updates
  const prevPriceLockRef = useRef(Boolean(priceLock));

  // Use useLayoutEffect for synchronous state sync - runs synchronously after DOM mutations
  useLayoutEffect(() => {
    const currentPriceLock = Boolean(priceLock);
    if (prevPriceLockRef.current !== currentPriceLock) {
      // Only update if value actually changed (prevents unnecessary state updates)
      prevPriceLockRef.current = currentPriceLock;
      setChecked(currentPriceLock);
    }
  }, [priceLock]);

  // Single handler - onChange is sufficient and handles both click and keyboard events
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const newValue = event.target.checked;

      // Force immediate DOM update for instant visual feedback using flushSync
      // This bypasses React's batching and updates the DOM synchronously
      flushSync(() => {
        setChecked(newValue);
        prevPriceLockRef.current = newValue; // Update ref immediately
      });

      // Defer parent state update to not block visual feedback
      // This ensures the switch moves instantly, and parent update happens after
      startTransition(() => {
        const updateFn = updateData as typeof updateData & {
          immediate?: (rowIndex: number, columnId: string, value: string | boolean) => void;
        };
        if (updateFn.immediate) {
          updateFn.immediate(row.index, column.id || '', newValue);
        } else {
          updateData(row.index, column.id || '', newValue);
        }
      });
    },
    [updateData, row.index, column.id],
  );

  return <Switch color="error" title="switch" checked={checked} onChange={handleChange} size="small" />;
}, arePriceLockPropsEqual);

PriceLockSwitchCell.displayName = 'PriceLockSwitchCell';

// Custom comparison function for memo - only re-render if mandatoryProductLock actually changed
const areMandatoryLockPropsEqual = (
  prevProps: CellContext<NationalProductsDetails, unknown>,
  nextProps: CellContext<NationalProductsDetails, unknown>,
) => {
  // Only re-render if the actual lock value changed or if row index changed
  const prevLock = Boolean(prevProps.row.original.mandatoryProductLock);
  const nextLock = Boolean(nextProps.row.original.mandatoryProductLock);
  const prevRowIndex = prevProps.row.index;
  const nextRowIndex = nextProps.row.index;

  return prevLock === nextLock && prevRowIndex === nextRowIndex;
};

export const MandatoryLockSwitchCell = memo(({ row, table, column }: CellContext<NationalProductsDetails, unknown>) => {
  const { original } = row;
  const { mandatoryProductLock } = original;
  const { updateData } = table.options.meta as CustomTableMeta<NationalProductsDetails>;

  // Use local state for instant visual feedback - updates immediately on click
  const [checked, setChecked] = useState(Boolean(mandatoryProductLock));

  // Track previous value to avoid unnecessary updates
  const prevMandatoryLockRef = useRef(Boolean(mandatoryProductLock));

  // Use useLayoutEffect for synchronous state sync - runs synchronously after DOM mutations
  useLayoutEffect(() => {
    const currentMandatoryLock = Boolean(mandatoryProductLock);
    if (prevMandatoryLockRef.current !== currentMandatoryLock) {
      // Only update if value actually changed (prevents unnecessary state updates)
      prevMandatoryLockRef.current = currentMandatoryLock;
      setChecked(currentMandatoryLock);
    }
  }, [mandatoryProductLock]);

  // Single handler - onChange is sufficient and handles both click and keyboard events
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const newValue = event.target.checked;

      // Force immediate DOM update for instant visual feedback using flushSync
      // This bypasses React's batching and updates the DOM synchronously
      flushSync(() => {
        setChecked(newValue);
        prevMandatoryLockRef.current = newValue; // Update ref immediately
      });

      // Defer parent state update to not block visual feedback
      // This ensures the switch moves instantly, and parent update happens after
      startTransition(() => {
        const updateFn = updateData as typeof updateData & {
          immediate?: (rowIndex: number, columnId: string, value: string | boolean) => void;
        };
        if (updateFn.immediate) {
          updateFn.immediate(row.index, column.id || '', newValue);
        } else {
          updateData(row.index, column.id || '', newValue);
        }
      });
    },
    [updateData, row.index, column.id],
  );

  return <Switch color="error" title="switch" checked={checked} onChange={handleChange} size="small" />;
}, areMandatoryLockPropsEqual);

MandatoryLockSwitchCell.displayName = 'MandatoryLockSwitchCell';

// Custom comparison function for memo - only re-render if isVisible actually changed
const areVisibilityPropsEqual = (
  prevProps: CellContext<NationalProductsDetails, unknown>,
  nextProps: CellContext<NationalProductsDetails, unknown>,
) => {
  // Only re-render if the actual visibility value changed or if row index changed
  const prevVisible = Boolean(prevProps.row.original.isVisible);
  const nextVisible = Boolean(nextProps.row.original.isVisible);
  const prevRowIndex = prevProps.row.index;
  const nextRowIndex = nextProps.row.index;

  return prevVisible === nextVisible && prevRowIndex === nextRowIndex;
};

export const VisibilitySwitchCell = memo(({ row, table, column }: CellContext<NationalProductsDetails, unknown>) => {
  const { original } = row;
  const { isVisible } = original;
  const { updateData } = table.options.meta as CustomTableMeta<NationalProductsDetails>;

  // Use local state for instant visual feedback - updates immediately on click
  const [checked, setChecked] = useState(Boolean(isVisible));

  // Track previous value to avoid unnecessary updates
  const prevIsVisibleRef = useRef(Boolean(isVisible));

  // Use useLayoutEffect for synchronous state sync - runs synchronously after DOM mutations
  useLayoutEffect(() => {
    const currentIsVisible = Boolean(isVisible);
    if (prevIsVisibleRef.current !== currentIsVisible) {
      // Only update if value actually changed (prevents unnecessary state updates)
      prevIsVisibleRef.current = currentIsVisible;
      setChecked(currentIsVisible);
    }
  }, [isVisible]);

  // Single handler - onChange is sufficient and handles both click and keyboard events
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const newValue = event.target.checked;

      // Force immediate DOM update for instant visual feedback using flushSync
      // This bypasses React's batching and updates the DOM synchronously
      flushSync(() => {
        setChecked(newValue);
        prevIsVisibleRef.current = newValue; // Update ref immediately
      });

      // Defer parent state update to not block visual feedback
      // This ensures the switch moves instantly, and parent update happens after
      startTransition(() => {
        const updateFn = updateData as typeof updateData & {
          immediate?: (rowIndex: number, columnId: string, value: string | boolean) => void;
        };
        if (updateFn.immediate) {
          updateFn.immediate(row.index, column.id || '', newValue);
        } else {
          updateData(row.index, column.id || '', newValue);
        }
      });
    },
    [updateData, row.index, column.id],
  );

  return <Switch color="error" title="switch" checked={checked} onChange={handleChange} size="small" />;
}, areVisibilityPropsEqual);

VisibilitySwitchCell.displayName = 'VisibilitySwitchCell';
